'use client';

import * as React from 'react';

/** Blocco etichetta + controllo per i form di amministrazione (pannelli admin, con input di dimensione sm). */
const AdminField = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => {
	const id = React.useId();
	const control = React.isValidElement<{ id?: string }>(children)
		? React.cloneElement(children, { id })
		: children;
	return (
		<div>
			<label htmlFor={id} className="mb-1 block text-xs font-semibold text-text-muted">
				{label}
			</label>
			{control}
		</div>
	);
};
AdminField.displayName = 'AdminField';

export interface AdminInputProps {
	/** Id HTML del campo; iniettato automaticamente da {@link AdminField} per collegare la label. */
	id?: string;
	/** Tipo di input HTML. Default: `text`. */
	type?: 'text' | 'email' | 'password' | 'number' | 'date';
	/** Valore corrente del campo. */
	value: string;
	/** Chiamata a ogni digitazione con il nuovo valore testuale. */
	onChange: (v: string) => void;
	/** Testo segnaposto mostrato quando il campo è vuoto. */
	placeholder?: string;
}

/** Campo di testo per i form di amministrazione, con lo stile standard dei pannelli admin. */
const AdminInput = ({ id, type = 'text', value, onChange, placeholder }: AdminInputProps) => (
	<input
		id={id}
		type={type}
		value={value}
		onChange={(e) => onChange(e.target.value)}
		placeholder={placeholder}
		className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-text outline-none focus:border-accent"
	/>
);
AdminInput.displayName = 'AdminInput';

export interface AdminSelectProps {
	/** Id HTML del select; iniettato automaticamente da {@link AdminField}/{@link AdminDividedRow} per collegare la label. */
	id?: string;
	/** Valore selezionato corrente. */
	value: string;
	/** Chiamata alla selezione di una nuova opzione, con il nuovo valore. */
	onChange: (v: string) => void;
	/** Elementi `<option>` del select. */
	children: React.ReactNode;
	/** Se true, il select è disabilitato e non selezionabile. */
	disabled?: boolean;
	/** `compact` = select inline in una riga; `full` = select a larghezza intera del form. Default: `full`. */
	size?: 'full' | 'compact';
	/** Id dell'elemento che etichetta il select (usato da {@link AdminDividedRow} quando la label non è un `<label>` collegabile via `htmlFor`). */
	'aria-labelledby'?: string;
}

/** Menu a tendina per i form di amministrazione, con due varianti di ingombro (compatta o a piena larghezza). */
const AdminSelect = ({
	id,
	value,
	onChange,
	children,
	disabled,
	size = 'full',
	'aria-labelledby': ariaLabelledBy,
}: AdminSelectProps) => (
	<select
		id={id}
		value={value}
		onChange={(e) => onChange(e.target.value)}
		disabled={disabled}
		aria-labelledby={ariaLabelledBy}
		className={
			size === 'compact'
				? 'rounded-md border border-border bg-background px-2 py-1 text-xs text-text outline-none focus:border-accent disabled:opacity-50'
				: 'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-text outline-none focus:border-accent'
		}
	>
		{children}
	</select>
);
AdminSelect.displayName = 'AdminSelect';

export interface AdminCheckboxRowProps {
	/** Stato corrente della checkbox. */
	checked: boolean;
	/** Chiamata al click, con il nuovo stato. */
	onChange: (v: boolean) => void;
	/** Etichetta mostrata accanto alla checkbox. */
	label: React.ReactNode;
}

/** Riga con checkbox e etichetta, bordata come le altre righe dei form di amministrazione. */
const AdminCheckboxRow = ({ checked, onChange, label }: AdminCheckboxRowProps) => (
	<label className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
		<input
			type="checkbox"
			checked={checked}
			onChange={(e) => onChange(e.target.checked)}
			className="h-4 w-4 accent-[var(--color-accent)]"
		/>
		<span className="text-sm text-text">{label}</span>
	</label>
);
AdminCheckboxRow.displayName = 'AdminCheckboxRow';

export interface AdminErrorNoticeProps {
	/** Messaggio di errore da mostrare. */
	children: React.ReactNode;
}

/** Avviso di errore (rosso) per i form di amministrazione. */
const AdminErrorNotice = ({ children }: AdminErrorNoticeProps) => (
	<p className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
		{children}
	</p>
);
AdminErrorNotice.displayName = 'AdminErrorNotice';

export interface AdminDividedListProps {
	/** Righe {@link AdminDividedRow} (o altro contenuto) separate da un divisore orizzontale. */
	children: React.ReactNode;
}

/** Lista di righe etichetta + select compatto separate da divisori (es. assegnazione ruoli per organizzazione). */
const AdminDividedList = ({ children }: AdminDividedListProps) => (
	<div className="divide-y divide-border rounded-lg border border-border bg-card">{children}</div>
);
AdminDividedList.displayName = 'AdminDividedList';

export interface AdminDividedRowProps {
	/** Etichetta principale della riga. */
	label: React.ReactNode;
	/** Testo secondario mostrato accanto all'etichetta. */
	sub?: React.ReactNode;
	/** Controllo mostrato a destra della riga (es. select, badge). */
	control: React.ReactNode;
}

/** Singola riga di {@link AdminDividedList}: etichetta a sinistra, controllo a destra. */
const AdminDividedRow = ({ label, sub, control }: AdminDividedRowProps) => {
	const labelId = React.useId();
	const controlWithLabel = React.isValidElement<{ 'aria-labelledby'?: string }>(control)
		? React.cloneElement(control, { 'aria-labelledby': labelId })
		: control;
	return (
		<div className="flex items-center gap-3 px-3 py-2">
			<span id={labelId} className="flex-1 truncate text-sm text-text">
				{label}
				{sub && <span className="ml-1 text-[12px] text-text-muted">{sub}</span>}
			</span>
			{controlWithLabel}
		</div>
	);
};
AdminDividedRow.displayName = 'AdminDividedRow';

export {
	AdminCheckboxRow,
	AdminDividedList,
	AdminDividedRow,
	AdminErrorNotice,
	AdminField,
	AdminInput,
	AdminSelect,
};
